import type { DateOption } from './model.ts';
import { localParts } from './time.ts';

/**
 * A guest's spoken answer, turned into the answer form: "Saturday works but not Sunday", "count me
 * in", "sorry, can't make it", "maybe the second one". The phone shows what it understood and the
 * guest taps to send, so a misheard word is corrected before anything is stored.
 */

export type ParsedReply = { status: 'in' | 'out' | 'maybe'; canMake: string[]; understood: string };

const DAY_NAMES: Record<string, string> = { sun: 'sunday', mon: 'monday', tue: 'tuesday', wed: 'wednesday', thu: 'thursday', fri: 'friday', sat: 'saturday' };
/** "The second one" counts down the list as shown; "the earlier one" goes by the calendar. */
const POSITIONS: Array<[RegExp, number]> = [
  [/\b(first|1st|former)\b/, 0],
  [/\b(second|2nd|latter)\b/, 1],
  [/\b(third|3rd)\b/, 2],
];
const EARLIER = /\b(?:earlier|earliest|sooner)(?: one| date| day| option)?\b/;
const LATER = /\b(?:later|latest)(?: one| date| day| option)\b|\bthe (?:later|latest)\b/;
/** Sayings that carry a "no" but mean yes: "can't wait", "not a problem", "won't be an issue", "no worries". */
const YES_DESPITE_NO =
  /\b(?:can'?t|cannot) wait\b|\b(?:not|no|never|isn'?t|won'?t|wouldn'?t|shouldn'?t)(?: be)?(?: a| an| any)? (?:problem|issue|trouble|bother)s?(?: at all)?\b|\b(?:no|not to|don'?t) worr(?:y|ies)\b|\b(?:wouldn'?t|won'?t|can'?t) miss(?: it)?\b|\bnothing(?:'s| is)? stopping me\b|\bnot busy\b|\bdon'?t see why not\b|\bwhy not\b/g;
const NEGATION = /\b(not|no|nope|can'?t|cannot|can not|won'?t|isn'?t|doesn'?t|don'?t|except|but not|busy|out|away)\b/;
const ALL_OUT =
  /\b(can'?t make it|cannot make it|can'?t come|won'?t make it|not coming|count me out|i'?m out|i am out|have to pass|can'?t do (?:either|any|both)|neither|none of (?:them|those)|(?:absolutely|definitely|certainly|of course) not|no way)\b|^(?:no|nope|sorry)\b(?!.*\b(?:works?|fine|good|can)\b)/;
const MAYBE = /\b(maybe|might|not sure|possibly|probably|tentative|i'?ll try|hopefully)\b/;
const ALL_IN = /\b(both|either|any(?: of them| day)?|all of them|whichever|all work|count me in|i'?m in|i am in|yes|yeah|yep|sure|absolutely|definitely|wouldn'?t miss it|of course|fine)\b/;
/** A clause that takes a side on its own, as opposed to only naming a day ("Saturday?"). */
const TAKES_A_SIDE = /\b(works?|fine|good|great|perfect|free|can|could|yes|yeah|yep|sure|in|there|ok|okay|available|love|happy|definitely|absolutely)\b/;
/** "Busy Saturday morning" rules out only a plan that starts in the morning. */
const PARTS_OF_DAY: Array<[RegExp, number, number]> = [
  [/\bmorning\b/, 0, 12],
  [/\b(?:afternoon|lunch(?:time)?)\b/, 11, 17],
  [/\b(?:during the day|daytime|day time)\b/, 0, 17],
  [/\b(?:evening|night)\b/, 17, 24],
];
/** A clause with no day that points back at the one before: "but free that evening". */
const REFERS_BACK = /\b(?:that|the same) (?:evening|night|day|afternoon|morning|one|date)\b|\bthen\b/;

/** Which options a clause names: by weekday ("Saturday", "Sat"), by position ("the second one"), or by date ("the earlier one"). */
function optionsIn(clause: string, options: DateOption[], tz: string): string[] {
  const hits = new Set<string>();
  options.forEach((o) => {
    const wd = localParts(new Date(o.start), tz).weekday;
    const day = DAY_NAMES[wd]!;
    if (new RegExp(`\\b(${day}s?|${wd})\\b`).test(clause)) hits.add(o.id);
  });
  for (const [re, i] of POSITIONS) if (re.test(clause) && options[i]) hits.add(options[i]!.id);
  // A moved plan appends its new date, so the list isn't always in date order.
  const byDate = [...options].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  if (EARLIER.test(clause) && byDate[0]) hits.add(byDate[0].id);
  if (LATER.test(clause) && byDate.length > 1) hits.add(byDate.at(-1)!.id);
  return [...hits];
}

export function parseGuestReply(text: string, options: DateOption[], tz: string): ParsedReply | undefined {
  const said = text
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/[^a-z0-9' ]+/g, ' , ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(YES_DESPITE_NO, 'fine');
  if (!said) return undefined;
  const all = options.map((o) => o.id);
  const label = (ids: string[]) =>
    ids
      .map((id) => options.find((o) => o.id === id)!)
      .map((o) => DAY_NAMES[localParts(new Date(o.start), tz).weekday]!.replace(/^./, (c) => c.toUpperCase()))
      .join(' and ');
  const hourOf = (id: string) => localParts(new Date(options.find((o) => o.id === id)!.start), tz).hour;
  // "anything but Friday" and "except Friday" are both "not Friday"; then split into clauses on
  // "but", "though" and commas: "saturday works , but not sunday".
  const plain = said.replace(/\b(?:anything|any day|any of them|either one|either|whichever) but\b/g, ', not').replace(/\bexcept(?: for)?\b/g, ', not');
  const clauses = plain.split(/\s*(?:,|\bbut\b|\bthough\b|\bhowever\b)\s*/).filter(Boolean);
  const yes = new Set<string>();
  const no = new Set<string>();
  // "Maybe Saturday": a day they're unsure about counts only when nothing is a firm yes.
  const unsure = new Set<string>();
  let hedged = false;
  // The days the last clause was about, and whether it only named them ("Saturday? … sadly no").
  let prev: { ids: string[]; open: boolean } | undefined;
  for (const clause of clauses) {
    const named = optionsIn(clause, options, tz);
    const hedge = MAYBE.test(clause);
    const negative = NEGATION.test(clause) && !hedge;
    if (!named.length) {
      if (hedge) hedged = true;
      // The answer can come after the day: "Saturday? Absolutely not.", "busy Saturday morning, but free that evening".
      if (prev && (prev.open || REFERS_BACK.test(clause)) && (negative || TAKES_A_SIDE.test(clause))) {
        for (const id of prev.ids) {
          (negative ? no : yes).add(id);
          (negative ? yes : no).delete(id);
        }
        prev = { ids: prev.ids, open: false };
      }
      continue;
    }
    if (negative) {
      const parts = PARTS_OF_DAY.filter(([re]) => re.test(clause));
      const ruledOut = parts.length ? named.filter((id) => parts.some(([, from, to]) => hourOf(id) >= from && hourOf(id) < to)) : named;
      ruledOut.forEach((id) => no.add(id));
      // Busy for only part of the day: nothing said yet about the plan's own time.
      const untouched = named.filter((id) => !ruledOut.includes(id));
      prev = untouched.length ? { ids: untouched, open: true } : { ids: named, open: false };
    } else if (hedge) {
      named.forEach((id) => unsure.add(id));
      prev = { ids: named, open: false };
    } else {
      named.forEach((id) => yes.add(id));
      if (/\bonly\b/.test(clause)) all.filter((id) => !named.includes(id)).forEach((id) => no.add(id));
      prev = { ids: named, open: !TAKES_A_SIDE.test(clause) };
    }
  }
  const maybe = MAYBE.test(said);
  const days = yes.size ? yes : unsure;
  if (days.size) {
    const canMake = [...days].filter((id) => !no.has(id));
    if (!canMake.length) return { status: 'out', canMake: [], understood: "You can't make any of the dates" };
    const hedging = hedged || !yes.size;
    return { status: hedging ? 'maybe' : 'in', canMake, understood: `${hedging ? 'Maybe' : 'Yes'} for ${label(canMake)}` };
  }
  // Only days they can't do, so the rest is a yes: "anything but Friday", "I'm out Sunday".
  if (no.size) {
    const canMake = all.filter((id) => !no.has(id));
    if (canMake.length) return { status: maybe ? 'maybe' : 'in', canMake, understood: `${maybe ? 'Maybe' : 'Yes'} for ${label(canMake)}, not ${label([...no])}` };
    return { status: 'out', canMake: [], understood: "You can't make any of the dates" };
  }
  if (ALL_OUT.test(said)) return { status: 'out', canMake: [], understood: "You can't make it" };
  if (maybe) return { status: 'maybe', canMake: all, understood: `Maybe, for ${all.length > 1 ? 'any of the dates' : label(all)}` };
  if (ALL_IN.test(said)) return { status: 'in', canMake: all, understood: `Yes, ${all.length > 1 ? 'any of the dates' : label(all)}` };
  return undefined;
}
